import { useState, useEffect, useRef } from "react";
import ChatHeader from "./components/ChatHeader";
import MessagesArea from "./components/MessagesArea";
import ChatInput from "./components/ChatInput";
import type { Message } from "./types/session";
import { fetchWithAuth, getAuthToken } from "./utils/auth";

import { useLocation } from "react-router-dom";

interface SessionData {
  id: string;
  client: string;
  consultant: string;
  consultantImage?: string | null;
  startTime: string;
  duration: string; // formatted as MM:SS
  balance: string;
  maxDuration: number;
  status: "active" | "ending" | "error";
  theme?: string;
}

const MIN_AI_MESSAGE_DELAY_MS = 650;
const MAX_AI_MESSAGE_DELAY_MS = 2800;
const BASE_AI_MESSAGE_DELAY_MS = 450;
const AI_DELAY_PER_WORD_MS = 55;
const AI_DELAY_PER_CHAR_MS = 4;
const OUT_OF_BALANCE_REDIRECT_DELAY_MS = 2800;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getDynamicMessageDelayMs(content: string): number {
  const trimmed = content.trim();
  const words = trimmed ? trimmed.split(/\s+/).length : 0;
  const chars = trimmed.length;
  const jitterMs = Math.floor(Math.random() * 180);

  const calculatedDelay =
    BASE_AI_MESSAGE_DELAY_MS +
    words * AI_DELAY_PER_WORD_MS +
    chars * AI_DELAY_PER_CHAR_MS +
    jitterMs;

  return Math.min(
    MAX_AI_MESSAGE_DELAY_MS,
    Math.max(MIN_AI_MESSAGE_DELAY_MS, calculatedDelay),
  );
}

export default function ChatApp() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const chatId = queryParams.get("chatId"); // Retrieves the chatId from the query string

  const [activeSessionId] = useState(chatId);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [elapsedSecondsInfo, setElapsedSeconds] = useState(0);

  const [activeSession, setActiveSession] = useState<SessionData | undefined>(
    undefined,
  );
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);
  const isEndingSessionRef = useRef(false);
  const hasEndedSessionRef = useRef(false);

  const handleOutOfBalanceFlow = () => {
    if (hasEndedSessionRef.current) {
      return;
    }

    setSessionNotice("Ficou sem saldo para continuar a sua consulta.");
    setIsTyping(false);
    setIsLoading(false);

    void handleEndSession({ redirect: false, keepalive: false });

    window.setTimeout(() => {
      window.location.href = "/user-dashboard";
    }, OUT_OF_BALANCE_REDIRECT_DELAY_MS);
  };

  useEffect(() => {
    const loadChatData = async () => {
      const response = await fetchWithAuth("/api/conversations/info", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversationId: chatId }),
      });
      if (!response.ok) {
        console.error("Failed to load chat data");
        return;
      }

      const data = await response.json();
      // console.log("Chat data loaded:", data);

      const performerImage = data.performer.image;

      // console.log("Performer image:", performerImage);

      setActiveSession({
        id: data.conversation.id,
        client: data.conversation.consultantName,
        consultant: data.conversation.consultant,
        consultantImage: performerImage,
        startTime: new Date(data.conversation.startDate).toLocaleTimeString(
          "pt-PT",
          { hour: "2-digit", minute: "2-digit" },
        ),
        duration: formatDuration(0),
        balance: formatDuration(parseFloat(data.conversation.maxDuration)),
        maxDuration: parseFloat(data.conversation.maxDuration),
        status: data.conversation.status,
        theme: Array.isArray(data.performer?.type) ? data.performer.type.join(', ') : data.performer?.type,
      });

      // Fetch sessions and messages from backend API here
      // For now, we use mock data
    };

    const loadMessages = async () => {
      if (!chatId) {
        console.error("Chat ID is missing");
        return;
      }

      const msgResponse = await fetchWithAuth(
        `/api/conversations/${chatId}/messages`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!msgResponse.ok) {
        console.error("Failed to load messages");
        return;
      }

      const msgData = await msgResponse.json();
      // console.log("Messages loaded:", msgData);

      // Map messages from backend format to Message interface
      if (
        msgData.messages &&
        Array.isArray(msgData.messages) &&
        msgData.messages.length > 0
      ) {
        const allMessages: Message[] = msgData.messages.map((msg: any) => ({
          id: msg.id || `msg-${Date.now()}-${Math.random()}`,
          sender: msg.role === "assistant" ? "assistant" : "client",
          content: msg.content,
          timestamp: new Date(msg.createdAt).toLocaleTimeString("pt-PT", {
            hour: "2-digit",
            minute: "2-digit",
          }),
        }));

        // Display all messages with 1-second delay between each
        setIsTyping(true);
        let currentMessages: Message[] = [];

        for (let i = 0; i < allMessages.length; i++) {
          // Wait 1 second before displaying each message
          await new Promise((resolve) => setTimeout(resolve, 1000));

          currentMessages = [...currentMessages, allMessages[i]];
          setMessages(currentMessages);
        }

        setIsTyping(false);
      }
    };

    loadChatData();
    loadMessages();
  }, [chatId]);

  // Timer: increment duration every second

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    let elapsedSeconds = 0;

    const timer = setInterval(() => {
      elapsedSeconds += 1;

      setElapsedSeconds(elapsedSeconds);

      setActiveSession((current) => {
        if (!current) return current;

        const totalTimeSeconds = current.maxDuration || 0;

        // Check if time is up
        if (totalTimeSeconds > 0 && elapsedSeconds >= totalTimeSeconds) {
          clearInterval(timer);
          // End session after a small delay to ensure state is updated
          setTimeout(() => {
            // console.log(
            //   "Session time limit reached. Ending session:",
            //   current.id,
            // );
            handleOutOfBalanceFlow();

          }, 100);
        }

        return {
          ...current,
          duration: formatDuration(elapsedSeconds),
        };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  setTimeout(() => {
    // fetch data every 10 seconds to update session status, balance, etc. /api/conversations/live/info
  }, 10000);

  useEffect(() => {
    const endSessionOnClose = () => {
      void handleEndSession({ redirect: false, keepalive: true });
    };

    window.addEventListener("beforeunload", endSessionOnClose);
    window.addEventListener("pagehide", endSessionOnClose);

    return () => {
      window.removeEventListener("beforeunload", endSessionOnClose);
      window.removeEventListener("pagehide", endSessionOnClose);
    };
  }, [activeSessionId]);

  const handleSendMessage = async (content: string) => {
    if (isLoading || isTyping) {
      console.warn("Cannot send message while waiting for response");
      return;
    }

    // Add user message to messages list
    const newMessage: Message = {
      id: Date.now().toString(),
      sender: "client",
      content,
      timestamp: new Date().toLocaleTimeString("pt-PT", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
    setMessages([...messages, newMessage]);

    // Set loading state to prevent further messages
    setIsLoading(true);
    setIsTyping(true);

    try {
      const response = await fetchWithAuth("/api/conversations/OnSendMessage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conversationId: chatId,
          message: content,
        }),
      });

      if (!response.ok) {
        console.error("Failed to send message");

        if (
          activeSession?.maxDuration &&
          elapsedSecondsInfo >= activeSession.maxDuration
        ) {
          handleOutOfBalanceFlow();
          return;
        }

        setIsLoading(false);
        setIsTyping(false);
        return;
      }

      const responseData = await response.json();

      // Process all messages from the replies array
      let responseMessages: Message[] = [];

      if (responseData.message) {
        try {
          // Parse the JSON string from the message field
          const parsedMessage = JSON.parse(responseData.message);

          if (parsedMessage.replies && Array.isArray(parsedMessage.replies)) {
            responseMessages = parsedMessage.replies.map(
              (msg: string, index: number) => ({
                id: `response-${Date.now()}-${index}`,
                sender: "assistant",
                content: msg,
                timestamp: new Date().toLocaleTimeString("pt-PT", {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
              }),
            );
          }
        } catch (parseError) {
          console.error("Error parsing message JSON:", parseError);
        }
      }

      // Display responses with dynamic delay based on message size.
      let currentMessages = [...messages, newMessage];

      for (let i = 0; i < responseMessages.length; i++) {
        const delayMs = getDynamicMessageDelayMs(responseMessages[i].content);
        await sleep(delayMs);

        currentMessages = [...currentMessages, responseMessages[i]];
        setMessages(currentMessages);
      }

      setIsTyping(false);
    } catch (error) {
      console.error("Error sending message:", error);
      setIsTyping(false);
    } finally {
      setIsLoading(false);
    }
  };

  async function handleEndSession(options?: { redirect?: boolean; keepalive?: boolean }) {
    const { redirect = true, keepalive = false } = options || {};

    if (!chatId || isEndingSessionRef.current || hasEndedSessionRef.current) {
      return;
    }

    isEndingSessionRef.current = true;

    const token = getAuthToken();
    const response = await fetch("/api/conversations/endChat", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        conversationId: chatId,
        chatDuration: elapsedSecondsInfo,
      }),
      keepalive,
    }).catch((error) => {
      console.error("Failed to end session:", error);
      return null;
    });

    if (!response || !response.ok) {
      isEndingSessionRef.current = false;
      setIsLoading(false);
      setIsTyping(false);
      return;
    }

    hasEndedSessionRef.current = true;

    if (redirect) {
      window.location.href = "/user-dashboard";
    }
  }

  if (!activeSession) return null;

  return (
    <div className="flex w-full h-screen overflow-hidden bg-gradient-to-b from-[#1c2039] via-bg-main to-[#020308] text-text">
      <div className="flex-1 flex flex-col min-w-0 h-full">
        <ChatHeader
          session={activeSession}
          onEndSession={() => {
            void handleEndSession({ redirect: true, keepalive: false });
          }}
          onAddTime={() => {}}
          onAddNote={() => {}}
          mode="user"
        />
        <section className="flex flex-col min-w-0 min-h-0 bg-gradient-to-br from-[#181b2a] via-[#070814] to-bg-main flex-1 overflow-hidden">
          {sessionNotice && (
            <div className="mx-3 mt-3 rounded-xl border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200 flex-shrink-0">
              {sessionNotice}
            </div>
          )}
          <MessagesArea
            messages={messages}
            mode="user"
            consultantName={activeSession.client}
            consultantImage={activeSession.consultantImage}
            isTyping={isTyping}
          />
          <ChatInput
            onSendMessage={handleSendMessage}
            balance={activeSession.balance}
            mode="user"
            disabled={isLoading || isTyping || Boolean(sessionNotice)}
          />
        </section>
      </div>
    </div>
  );
}
